import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  BarChart3,
  CalendarDays,
  CloudRain,
  Database,
  Droplets,
  Gauge,
  RotateCcw,
  Search,
  Table2,
  Thermometer,
  Wind,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import api from "../api/api";
import "./ClimateHistory.css";

const PAGE_SIZE = 15;

const ClimateHistory = () => {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [climateData, setClimateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [dateValue, setDateValue] = useState({ dateStart: "", dateEnd: "" });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalDays: 0,
  });

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await api.get("/field/fields");
        const farms = Array.isArray(response.data?.farms)
          ? response.data.farms
          : [];
        const allFields = farms.flatMap((farm) =>
          Array.isArray(farm.fields) ? farm.fields : []
        );
        setFields(allFields);
        if (allFields.length > 0) setSelectedFieldId(allFields[0].id);
      } catch (requestError) {
        console.error("Error fetching fields:", requestError);
        setError(t("no_field_error"));
      }
    };

    fetchFields();
  }, [t]);

  const getClimateHistory = async (page = 1) => {
    if (!selectedFieldId) {
      setError(t("no_field_error"));
      return;
    }

    if ((dateValue.dateStart && !dateValue.dateEnd) || (!dateValue.dateStart && dateValue.dateEnd)) {
      setError(
        t("climate_date_range_incomplete", {
          defaultValue: "Please choose both a start date and an end date.",
        })
      );
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const params = new URLSearchParams();
      if (dateValue.dateStart) params.append("start", dateValue.dateStart);
      if (dateValue.dateEnd) params.append("end", dateValue.dateEnd);
      params.append("page", page);
      params.append("limit", PAGE_SIZE);

      const response = await api.get(
        `/climate/history/${selectedFieldId}?${params.toString()}`
      );
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setClimateData(data);
      const meta = response.data?.meta || {};
      setPagination({
        currentPage: Number(meta.page) || page,
        totalPages: Number(meta.total_pages) || (data.length > 0 ? 1 : 0),
        totalDays: Number(meta.total_days) || data.length,
      });

      if (data.length === 0) setError(t("no_data_error"));
    } catch (requestError) {
      console.error("Error fetching climate history:", requestError);
      setError(t("no_data_error"));
      setClimateData([]);
      setPagination({ currentPage: 1, totalPages: 0, totalDays: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDateValue({ dateStart: "", dateEnd: "" });
    setClimateData([]);
    setSearched(false);
    setError(null);
    setViewMode("table");
    setPagination({ currentPage: 1, totalPages: 0, totalDays: 0 });
  };

  const chartData = useMemo(
    () =>
      climateData.map((day) => ({
        date: moment(day.date).format("DD/MM"),
        tempMax: day.temperature_max ?? 0,
        tempMin: day.temperature_min ?? 0,
        humidityMax: day.humidity_max ?? 0,
        humidityMin: day.humidity_min ?? 0,
        pressure: day.pressure ?? 0,
        wind: day.windspeed ?? 0,
        rain: day.precipitation ?? 0,
        et0: day.ET0 ?? 0,
      })),
    [climateData]
  );

  const selectedField = fields.find(
    (field) => String(field.id) === String(selectedFieldId)
  );
  const periodLabel =
    dateValue.dateStart && dateValue.dateEnd
      ? `${moment(dateValue.dateStart).format("DD/MM/YYYY")} – ${moment(
          dateValue.dateEnd
        ).format("DD/MM/YYYY")}`
      : t("last_30_days", { defaultValue: "Last 30 days" });
  const firstRow = climateData.length ? (pagination.currentPage - 1) * PAGE_SIZE + 1 : 0;
  const lastRow = Math.min(firstRow + climateData.length - 1, pagination.totalDays);

  return (
    <main className="climate-history-page">
      <header className="climate-history-header">
        <div className="climate-history-heading">
          <span className="climate-history-eyebrow">SMARTFARM</span>
          <h1>{t("page_title")}</h1>
          <p>{t("page_subtitle")}</p>
        </div>
        <div className="climate-history-header-meta">
          <CalendarDays size={18} aria-hidden="true" />
          <div>
            <span>{t("period", { defaultValue: "Period" })}</span>
            <strong>{periodLabel}</strong>
          </div>
        </div>
      </header>

      <section className="climate-filter-card">
        <div className="climate-section-heading">
          <div>
            <span>{t("overview")}</span>
            <h2>{t("search_climate_data", { defaultValue: "Climate data filters" })}</h2>
          </div>
          <Search size={19} aria-hidden="true" />
        </div>

        <div className="climate-filter-grid">
          <Form.Group>
            <Form.Label>{t("field_label")}</Form.Label>
            <Form.Select
              value={selectedFieldId}
              required
              onChange={(event) => setSelectedFieldId(event.target.value)}
            >
              <option value="">{t("select_field_placeholder")}</option>
              {fields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>{t("start_date_label")}</Form.Label>
            <Form.Control
              type="date"
              value={dateValue.dateStart}
              onChange={(event) =>
                setDateValue((current) => ({
                  ...current,
                  dateStart: event.target.value,
                }))
              }
              max={dateValue.dateEnd || moment().format("YYYY-MM-DD")}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>{t("end_date_label")}</Form.Label>
            <Form.Control
              type="date"
              value={dateValue.dateEnd}
              onChange={(event) =>
                setDateValue((current) => ({
                  ...current,
                  dateEnd: event.target.value,
                }))
              }
              min={dateValue.dateStart}
              max={moment().format("YYYY-MM-DD")}
            />
          </Form.Group>

          <div className="climate-filter-actions">
            <button
              type="button"
              className="climate-primary-button"
              onClick={() => getClimateHistory()}
              disabled={loading || !selectedFieldId}
            >
              <Search size={16} aria-hidden="true" />
              {loading ? t("loading_text") : t("search_button")}
            </button>
            <button
              type="button"
              className="climate-secondary-button"
              onClick={handleReset}
              disabled={loading}
            >
              <RotateCcw size={16} aria-hidden="true" />
              {t("reset_button")}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="climate-message climate-message--warning" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label={t("close", { defaultValue: "Close" })}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {!searched && !error && (
        <div className="climate-message climate-message--info">
          <Database size={18} aria-hidden="true" />
          <span>{t("info_message")}</span>
        </div>
      )}

      {searched && climateData.length > 0 && (
        <section className="climate-results-summary">
          <div>
            <span className="climate-results-icon">
              <Database size={19} aria-hidden="true" />
            </span>
            <div>
              <span>{selectedField?.name || t("field_label")}</span>
              <strong>
                {pagination.totalDays} {t("results_found")}
              </strong>
            </div>
          </div>
          <span className="climate-results-period">{periodLabel}</span>
        </section>
      )}

      <section className="climate-table-card">
        <div className="climate-section-heading climate-table-heading">
          <div>
            <span>{t("page_title")}</span>
            <h2>{t("results", { defaultValue: "Results" })}</h2>
          </div>
          <div className="climate-view-switch" aria-label={t("overview")}>
            <button
              type="button"
              className={viewMode === "table" ? "is-active" : ""}
              onClick={() => setViewMode("table")}
              disabled={climateData.length === 0}
              aria-pressed={viewMode === "table"}
            >
              <Table2 size={15} aria-hidden="true" />
              {t("table", { defaultValue: "Table" })}
            </button>
            <button
              type="button"
              className={viewMode === "chart" ? "is-active" : ""}
              onClick={() => setViewMode("chart")}
              disabled={climateData.length === 0}
              aria-pressed={viewMode === "chart"}
            >
              <BarChart3 size={15} aria-hidden="true" />
              {t("chart", { defaultValue: "Chart" })}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="climate-empty-state" role="status">
            <span className="climate-spinner" />
            <h3>{t("loading_text")}</h3>
          </div>
        ) : climateData.length === 0 ? (
          <div className="climate-empty-state">
            <span className="climate-empty-icon">
              {searched ? <Database size={24} /> : <Search size={24} />}
            </span>
            <h3>
              {searched ? t("no_results_found") : t("select_field_prompt")}
            </h3>
            <p>
              {searched
                ? t("no_data_error")
                : t("info_message")}
            </p>
          </div>
        ) : viewMode === "table" ? (
          <div className="climate-table-scroll">
            <table className="climate-data-table">
              <thead>
                <tr>
                  <th scope="col">{t("date")}</th>
                  <th scope="col">{t("temp_max")}</th>
                  <th scope="col">{t("temp_min")}</th>
                  <th scope="col">{t("humidity_max")}</th>
                  <th scope="col">{t("humidity_min")}</th>
                  <th scope="col">{t("pressure")}</th>
                  <th scope="col">{t("wind")}</th>
                  <th scope="col">{t("rain")}</th>
                  <th scope="col">{t("et0")}</th>
                </tr>
              </thead>
              <tbody>
                {climateData.map((day, index) => (
                  <tr key={`${day.date}-${index}`}>
                    <td className="climate-date-cell">
                      <strong>{moment(day.date).format("DD/MM/YYYY")}</strong>
                      <span>{moment(day.date).format("dddd")}</span>
                    </td>
                    <td className="climate-value-hot">{day.temperature_max ?? "—"}</td>
                    <td className="climate-value-cold">{day.temperature_min ?? "—"}</td>
                    <td>{day.humidity_max ?? "—"}</td>
                    <td>{day.humidity_min ?? "—"}</td>
                    <td>{day.pressure ?? "—"}</td>
                    <td>{day.windspeed ?? "—"}</td>
                    <td className="climate-value-rain">{day.precipitation ?? "—"}</td>
                    <td>{day.ET0 ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {viewMode === "table" && pagination.totalPages > 1 && !loading && (
          <nav
            className="climate-pagination"
            aria-label={t("climate_pagination_label", {
              defaultValue: "Climate history pagination",
            })}
          >
            <span className="climate-pagination-summary">
              {firstRow}–{lastRow} {t("climate_pagination_of_days", {
                defaultValue: "of {{total}} days",
                total: pagination.totalDays,
              })}
            </span>
            <div className="climate-pagination-actions">
              <button
                type="button"
                onClick={() => getClimateHistory(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <ChevronLeft size={16} aria-hidden="true" />{" "}
                {t("previous", { defaultValue: "Previous" })}
              </button>
              <strong>
                {t("climate_pagination_page", {
                  defaultValue: "Page {{current}} / {{total}}",
                  current: pagination.currentPage,
                  total: pagination.totalPages,
                })}
              </strong>
              <button
                type="button"
                onClick={() => getClimateHistory(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {t("next", { defaultValue: "Next" })}{" "}
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
          </nav>
        )}

        {viewMode === "chart" && climateData.length > 0 && !loading && (
          <div className="climate-inline-charts">
            <div className="climate-chart-grid">
            <section className="climate-chart-card climate-chart-card--wide">
              <div className="climate-chart-card-title">
                <Thermometer size={18} />
                <h3>{t("Temperature_C")}</h3>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#edf1f3" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="tempMax" stroke="#d64e62" name={t("temp_max")} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="tempMin" stroke="#3f83c5" name={t("temp_min")} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </section>

            <section className="climate-chart-card climate-chart-card--wide">
              <div className="climate-chart-card-title">
                <Droplets size={18} />
                <h3>{t("Humidity_percent")}</h3>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#edf1f3" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="humidityMax" stroke="#26a6b7" name={t("humidity_max")} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="humidityMin" stroke="#71c4ce" name={t("humidity_min")} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </section>

            <section className="climate-chart-card">
              <div className="climate-chart-card-title">
                <CloudRain size={18} />
                <h3>{t("Precipitation_mm")}</h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#edf1f3" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="rain" fill="#26a6b7" name={t("rain")} radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </section>

            <section className="climate-chart-card">
              <div className="climate-chart-card-title">
                <Gauge size={18} />
                <h3>{t("Pressure_and_Wind")}</h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#edf1f3" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="pressure" stroke="#d79a22" name={t("pressure")} strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="wind" stroke="#e47b45" name={t("wind")} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </section>

            <section className="climate-chart-card climate-chart-card--wide">
              <div className="climate-chart-card-title">
                <Wind size={18} />
                <h3>{t("Evapotranspiration_ET0")}</h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#edf1f3" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="et0" fill="#526477" name={t("et0")} radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </section>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default ClimateHistory;
